import React, { useEffect, useState, useCallback } from 'react';
import { getTeams, createTeam, updateTeam, deleteTeam, addTeamMember, removeTeamMember, getEmployees } from '../../api';
import { getInitials } from '../../utils';

export default function ManageTeams() {
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [form, setForm] = useState({ name: '', head_employee_id: '' });
  const [addMemberTeam, setAddMemberTeam] = useState(null);
  const [addMemberEmp, setAddMemberEmp] = useState('');

  const load = useCallback(async () => {
    const [t, e] = await Promise.all([getTeams(), getEmployees()]);
    setTeams(t);
    setEmployees(e);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (editTeam) await updateTeam(editTeam.id, form);
    else await createTeam(form);
    setShowCreate(false); setEditTeam(null); setForm({ name: '', head_employee_id: '' });
    load();
  };

  const openEdit = (team) => {
    setEditTeam(team);
    setForm({ name: team.name, head_employee_id: team.head_employee_id || '' });
    setShowCreate(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team?')) return;
    await deleteTeam(id);
    load();
  };

  const handleAddMember = async () => {
    if (!addMemberEmp) return;
    await addTeamMember(addMemberTeam.id, addMemberEmp);
    setAddMemberTeam(null); setAddMemberEmp('');
    load();
  };

  const handleRemoveMember = async (teamId, empId) => {
    await removeTeamMember(teamId, empId);
    load();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Manage Teams</div>
          <div className="text-muted" style={{ fontSize: '0.875rem', marginTop: 2 }}>Create teams and assign employees</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditTeam(null); setForm({ name: '', head_employee_id: '' }); setShowCreate(true); }}>
          Create New Team
        </button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Team Name</th><th>Employees</th><th>Team Head</th><th></th></tr></thead>
            <tbody>
              {teams.length === 0 && <tr><td colSpan="4" className="empty">No teams yet</td></tr>}
              {teams.map(team => (
                <React.Fragment key={team.id}>
                  <tr>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>
                          {team.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600 }}>{team.name}</span>
                      </div>
                    </td>
                    <td>{team.members?.length || 0} Employees</td>
                    <td>{team.head?.name || <span className="text-muted">No team head assigned!</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setAddMemberTeam(team)}>+ Add Member</button>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(team)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(team.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  {team.members?.map(m => (
                    <tr key={m.id} style={{ background: '#fafbfc' }}>
                      <td colSpan="1" style={{ paddingLeft: 56 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="employee-avatar" style={{ width: 26, height: 26, fontSize: '0.7rem' }}>{getInitials(m.name)}</div>
                          <span style={{ fontSize: '0.875rem' }}>{m.name}</span>
                          <span className="badge badge-gray" style={{ fontSize: '0.7rem' }}>{m.role}</span>
                        </div>
                      </td>
                      <td className="text-muted" style={{ fontSize: '0.8rem' }}>{m.department}</td>
                      <td></td>
                      <td>
                        <button className="btn btn-outline btn-sm" onClick={() => handleRemoveMember(team.id, m.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Team Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{editTeam ? 'Edit Team' : 'Create New Team'}</div>
            <form onSubmit={handleCreate}>
              <div className="form-group"><label>Team Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Development Team" /></div>
              <div className="form-group">
                <label>Team Head (optional)</label>
                <select value={form.head_employee_id} onChange={e => setForm(f => ({ ...f, head_employee_id: e.target.value }))}>
                  <option value="">No team head</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editTeam ? 'Save Changes' : 'Create Team'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {addMemberTeam && (
        <div className="modal-overlay" onClick={() => setAddMemberTeam(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add Member to {addMemberTeam.name}</div>
            <div className="form-group">
              <label>Select Employee</label>
              <select value={addMemberEmp} onChange={e => setAddMemberEmp(e.target.value)}>
                <option value="">Select...</option>
                {employees.filter(e => !addMemberTeam.members?.find(m => m.id === e.id)).map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setAddMemberTeam(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddMember}>Add Member</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
